/**
 * MemberContent — Phase B + C dashboard content tabs.
 * One file, multiple section components. Each section calls /api/truth-releases
 * with a `has` or `exclusive` filter to surface the same data through different lenses.
 *
 * Sections exported:
 *   <WeeklyReleases/>   — every release the user can see
 *   <AudioLibrary/>     — has audio
 *   <WallpapersGrid/>   — has wallpapers (phone/desktop/tablet)
 *   <Printables/>       — has printable
 *   <TruthArchive/>     — emotion-tag filter
 *   <ExclusiveReleases/>— is_exclusive=true
 *   <CommunityPolls/>   — polls + comments
 *   <TruthSuggestions/> — Truth Maker submit-a-truth form
 *   <LiveQA/>           — Truth Maker live Q&A events
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Headphones, Download, Heart, Smartphone, Monitor, Tablet, Image as ImageIcon, FileText, Lightbulb, Calendar, Play, MessageCircle, Star } from "lucide-react";

const EMOTIONS = [
  ["hope","Hope"],["courage","Courage"],["failure","Failure"],["growth","Growth"],
  ["kindness","Kindness"],["purpose","Purpose"],["family","Family"],
  ["anxiety","Anxiety"],["grief","Grief"],
];

const fixUrl = (u) => !u ? "" : (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

function ReleaseCard({ rel }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] overflow-hidden hover:border-[#C9A055]/40 transition-colors" data-testid={`release-card-${rel.id}`}>
      {rel.image_url && <div className="aspect-[4/3] bg-black"><img src={fixUrl(rel.image_url)} alt={rel.title} className="w-full h-full object-cover"/></div>}
      <div className="p-4">
        <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">{(rel.ordinary_thing || "TRUTH").toUpperCase()}</div>
        <div className="font-display text-xl text-[#F4F1EA] mt-1">{rel.title}</div>
        {rel.lesson_summary && <p className="font-body text-[13px] text-[#B5B0A7] mt-2 leading-relaxed line-clamp-3">{rel.lesson_summary}</p>}
        {(rel.emotion_tags || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">{rel.emotion_tags.map((t) => <span key={t} className="px-2 py-0.5 border border-[#C9A055]/30 text-[#C9A055] text-[9px] tracking-[0.22em] uppercase">{t}</span>)}</div>
        )}
      </div>
    </div>
  );
}

function useReleases(params) {
  const [data, setData] = useState({ items: [], user_rank: 0 });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams(params || {}).toString();
    http.get(`/truth-releases${q ? "?" + q : ""}`).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [JSON.stringify(params)]);
  return { ...data, loading };
}

function EmptyState({ title, hint, icon: Icon }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-12 text-center">
      <Icon size={28} className="text-[#5a5650] mx-auto mb-3"/>
      <div className="font-display text-xl text-[#F4F1EA]">{title}</div>
      <p className="font-body text-[13px] text-[#8a857d] mt-2 max-w-md mx-auto">{hint}</p>
    </div>
  );
}

// ===== WEEKLY RELEASES =====
export function WeeklyReleases() {
  const { items, loading } = useReleases();
  if (loading) return <Skeleton/>;
  if (items.length === 0) return <EmptyState icon={ImageIcon} title="No releases yet" hint="Mickey will release new truths weekly. Once published, they'll appear here."/>;
  return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="weekly-grid">{items.map((r) => <ReleaseCard key={r.id} rel={r}/>)}</div>;
}

// ===== AUDIO LIBRARY =====
export function AudioLibrary() {
  const { items, loading } = useReleases({ has: "audio" });
  if (loading) return <Skeleton/>;
  if (items.length === 0) return <EmptyState icon={Headphones} title="Audio library is empty" hint="Audio versions read by Mickey will appear here. Stream or download."/>;
  return (
    <div className="space-y-3" data-testid="audio-list">
      {items.map((r) => (
        <div key={r.id} className="border border-white/10 bg-[#0a0a0a] p-4" data-testid={`audio-${r.id}`}>
          <div className="flex items-start gap-4 flex-wrap">
            {r.image_url && <img src={fixUrl(r.image_url)} alt={r.title} className="w-20 h-20 object-cover border border-white/10"/>}
            <div className="flex-1 min-w-0">
              <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">{r.ordinary_thing?.toUpperCase()}</div>
              <div className="font-display text-lg text-[#F4F1EA]">{r.title}</div>
              <audio controls src={fixUrl(r.audio_url)} className="mt-2 w-full max-w-md h-9"/>
              <a href={fixUrl(r.audio_url)} download className="inline-flex items-center gap-1 mt-2 font-ui text-[10px] tracking-[0.22em] text-[#C9A055] hover:underline uppercase"><Download size={11}/> Download MP3</a>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ===== WALLPAPERS =====
export function WallpapersGrid() {
  const { items, loading } = useReleases({ has: "wallpaper" });
  if (loading) return <Skeleton/>;
  if (items.length === 0) return <EmptyState icon={Smartphone} title="No wallpapers yet" hint="Phone, desktop, and tablet wallpapers for each truth will appear here."/>;
  return (
    <div className="space-y-6" data-testid="wallpapers-grid">
      {items.map((r) => (
        <div key={r.id} className="border border-white/10 bg-[#0a0a0a] p-5">
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">{r.ordinary_thing?.toUpperCase()}</div>
          <div className="font-display text-xl text-[#F4F1EA]">{r.title}</div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[["phone",Smartphone,"Phone"],["desktop",Monitor,"Desktop"],["tablet",Tablet,"Tablet"]].map(([k,Icon,label]) => {
              const url = r.wallpapers?.[k];
              return (
                <div key={k} className="border border-white/10 p-3 text-center">
                  <Icon size={20} className={`mx-auto mb-2 ${url ? "text-[#C9A055]" : "text-[#5a5650]"}`}/>
                  <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">{label.toUpperCase()}</div>
                  {url ? (
                    <a href={fixUrl(url)} download className="inline-flex items-center gap-1 mt-2 font-ui text-[10px] tracking-[0.22em] text-[#C9A055] hover:underline uppercase" data-testid={`wp-${k}-${r.id}`}>
                      <Download size={11}/> Download
                    </a>
                  ) : <div className="text-[10px] text-[#5a5650] mt-2">—</div>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ===== PRINTABLES =====
export function Printables() {
  const { items, loading } = useReleases({ has: "printable" });
  if (loading) return <Skeleton/>;
  if (items.length === 0) return <EmptyState icon={FileText} title="No printables yet" hint="High-res downloads, PDFs, and printable cards will appear here."/>;
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="printables-grid">
      {items.map((r) => (
        <div key={r.id} className="border border-white/10 bg-[#0a0a0a] p-5">
          {r.printable_url && <img src={fixUrl(r.printable_url)} alt={r.title} className="w-full aspect-[3/4] object-cover border border-white/10 mb-3"/>}
          <div className="font-display text-lg text-[#F4F1EA]">{r.title}</div>
          <div className="flex gap-2 mt-3 flex-wrap">
            {r.printable_url && <a href={fixUrl(r.printable_url)} download className="btn-outline text-[10px] inline-flex items-center gap-1" data-testid={`print-img-${r.id}`}><Download size={11}/> Image</a>}
            {r.printable_pdf_url && <a href={r.printable_pdf_url} target="_blank" rel="noreferrer" className="btn-outline text-[10px] inline-flex items-center gap-1" data-testid={`print-pdf-${r.id}`}><FileText size={11}/> PDF</a>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ===== TRUTH ARCHIVE =====
export function TruthArchive() {
  const [emotion, setEmotion] = useState("");
  const { items, loading } = useReleases(emotion ? { emotion } : {});
  const [meta, setMeta] = useState({ emotion_counts: {}, total: 0 });
  useEffect(() => { http.get("/truth-releases-meta").then(({data}) => setMeta(data)).catch(() => {}); }, []);

  return (
    <div className="space-y-5" data-testid="archive-section">
      <div className="border border-white/10 bg-[#0a0a0a] p-5">
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">FILTER BY EMOTION</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setEmotion("")} data-testid="filter-all" className={`px-3 py-1.5 border font-ui text-[10px] tracking-[0.22em] uppercase ${!emotion ? "border-[#C9A055] bg-[#C9A055] text-black" : "border-white/15 text-[#9a9690] hover:text-[#F4F1EA]"}`}>All ({meta.total || 0})</button>
          {EMOTIONS.map(([k, label]) => (
            <button key={k} onClick={() => setEmotion(k)} data-testid={`filter-${k}`} className={`px-3 py-1.5 border font-ui text-[10px] tracking-[0.22em] uppercase ${emotion === k ? "border-[#C9A055] bg-[#C9A055] text-black" : "border-white/15 text-[#9a9690] hover:text-[#F4F1EA]"}`}>
              {label} ({meta.emotion_counts?.[k] || 0})
            </button>
          ))}
        </div>
      </div>
      {loading ? <Skeleton/> : items.length === 0 ? (
        <EmptyState icon={Star} title="No truths under this emotion yet" hint="Try another tag or check back as the archive grows."/>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{items.map((r) => <ReleaseCard key={r.id} rel={r}/>)}</div>
      )}
    </div>
  );
}

// ===== EXCLUSIVE RELEASES =====
export function ExclusiveReleases() {
  const { items, loading } = useReleases({ exclusive: true });
  if (loading) return <Skeleton/>;
  if (items.length === 0) return <EmptyState icon={Star} title="No exclusive releases yet" hint="Members-only art that's never sold publicly will appear here."/>;
  return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exclusive-grid">{items.map((r) => <ReleaseCard key={r.id} rel={r}/>)}</div>;
}

// ===== COMMUNITY POLLS =====
export function CommunityPolls() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => http.get("/polls").then(({data}) => setPolls(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const vote = async (pid, optionKey) => {
    try { await http.post(`/polls/${pid}/vote`, { option_key: optionKey }); toast.success("Vote recorded"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not vote"); }
  };

  if (loading) return <Skeleton/>;
  if (polls.length === 0) return <EmptyState icon={MessageCircle} title="No polls yet" hint="Mickey will post community polls weekly — your tier unlocks them."/>;
  return <div className="space-y-4" data-testid="polls-list">{polls.map((p) => <PollCard key={p.id} poll={p} onVote={vote}/>)}</div>;
}

function PollCard({ poll, onVote }) {
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState("");
  const [open, setOpen] = useState(false);
  const total = Math.max(poll.total_votes || 1, 1);

  const loadComments = () => http.get(`/polls/${poll.id}/comments`).then(({data}) => setComments(data)).catch(() => {});
  useEffect(() => { if (open) loadComments(); }, [open]);

  const submit = async () => {
    if (!commentBody.trim()) return;
    try { await http.post(`/polls/${poll.id}/comments`, { body: commentBody }); setCommentBody(""); loadComments(); }
    catch { toast.error("Could not post"); }
  };

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5" data-testid={`poll-card-${poll.id}`}>
      <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">COMMUNITY POLL</div>
      <div className="font-display text-2xl text-[#F4F1EA] mt-2">{poll.question}</div>
      <div className="mt-4 space-y-2">
        {(poll.options || []).map((o) => {
          const pct = Math.round(100 * (o.votes || 0) / total);
          const mine = poll.my_vote === o.key;
          return (
            <button key={o.key} onClick={() => onVote(poll.id, o.key)} data-testid={`vote-${poll.id}-${o.key}`}
              className={`w-full text-left relative border bg-[#080808] px-4 py-3 overflow-hidden transition-colors ${mine ? "border-[#C9A055]" : "border-white/10 hover:border-white/30"}`}>
              <div className="absolute inset-y-0 left-0 bg-[#C9A055]/15" style={{ width: poll.my_vote ? `${pct}%` : 0 }}/>
              <div className="relative flex justify-between items-center gap-2">
                <span className="font-body text-[14px] text-[#F4F1EA]">{o.label} {mine && <span className="text-[#C9A055]">✓</span>}</span>
                {poll.my_vote && <span className="font-ui text-[11px] tracking-[0.22em] text-[#C9A055]">{pct}%</span>}
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex justify-between items-center">
        <span className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">{poll.total_votes || 0} VOTES</span>
        <button onClick={() => setOpen((v) => !v)} className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] uppercase" data-testid={`toggle-comments-${poll.id}`}>
          {open ? "Hide" : "View"} Discussion
        </button>
      </div>
      {open && (
        <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
          <div className="flex gap-2">
            <input value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder="Add your thought…" className="flex-1 bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-body text-[13px] focus:border-[#C9A055] focus:outline-none" data-testid={`comment-input-${poll.id}`}/>
            <button onClick={submit} className="px-4 py-2 bg-[#C9A055] text-black font-ui text-[10px] tracking-[0.32em] uppercase" data-testid={`comment-submit-${poll.id}`}>Post</button>
          </div>
          {comments.length === 0 ? (
            <p className="font-body italic text-[12px] text-[#8a857d]">Be the first to comment.</p>
          ) : comments.map((c) => (
            <div key={c.id} className="border-l-2 border-[#C9A055]/30 pl-3 py-1">
              <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055]">{c.user_name} · {new Date(c.created_at).toLocaleDateString()}</div>
              <div className="font-body text-[13px] text-[#F4F1EA] mt-1 whitespace-pre-line">{c.body}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== TRUTH SUGGESTIONS (Truth Maker) =====
export function TruthSuggestions({ canSubmit }) {
  const [body, setBody] = useState("");
  const [mine, setMine] = useState([]);
  const load = () => http.get("/my/truth-suggestions").then(({data}) => setMine(data)).catch(() => {});
  useEffect(() => { if (canSubmit) load(); }, [canSubmit]);
  const submit = async () => {
    if (body.trim().length < 4) return toast.error("Tell me more (at least 4 chars)");
    try { await http.post("/truth-suggestions", { suggestion: body }); setBody(""); toast.success("Suggestion received. Mickey reads every one."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!canSubmit) {
    return <EmptyState icon={Lightbulb} title="Truth Maker only" hint="Become a Truth Maker to suggest future truth concepts. Mickey reads every one."/>;
  }
  return (
    <div className="space-y-5" data-testid="suggestions-section">
      <div className="border border-[#C9A055]/30 bg-gradient-to-br from-[#1a1814] to-[#0a0a0a] p-6">
        <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">SUGGEST A TRUTH</div>
        <div className="font-display text-2xl text-[#F4F1EA] mt-2">What ordinary thing carries an extraordinary lesson?</div>
        <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="The empty chair at the dinner table. The keys you can't find. A streetlight..." className="mt-4 w-full bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-body text-[14px] focus:border-[#C9A055] focus:outline-none" data-testid="suggestion-input"/>
        <button onClick={submit} className="mt-3 px-5 py-2 bg-[#C9A055] text-black font-ui text-[10px] tracking-[0.32em] uppercase" data-testid="suggestion-submit">Send to Mickey</button>
      </div>
      {mine.length > 0 && (
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">YOUR SUGGESTIONS</div>
          {mine.map((s) => (
            <div key={s.id} className="border border-white/10 bg-[#0a0a0a] p-4 mb-2" data-testid={`my-suggestion-${s.id}`}>
              <div className="font-body text-[13px] text-[#F4F1EA] whitespace-pre-line">{s.suggestion}</div>
              <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase mt-2">{new Date(s.created_at).toLocaleDateString()} · {s.status}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== LIVE Q&A =====
export function LiveQA({ canView }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (canView) http.get("/live-qa").then(({data}) => setRows(data)).catch(() => {}); }, [canView]);
  if (!canView) return <EmptyState icon={Calendar} title="Truth Maker only" hint="Monthly live Q&As with Mickey + their recordings live here for Truth Makers."/>;
  if (rows.length === 0) return <EmptyState icon={Calendar} title="No events scheduled" hint="Mickey will announce the next live Q&A here. Stay tuned."/>;
  const upcoming = rows.filter((r) => !r.is_past);
  const past = rows.filter((r) => r.is_past);
  return (
    <div className="space-y-6" data-testid="liveqa-section">
      {upcoming.length > 0 && (
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">UPCOMING</div>
          <div className="space-y-3">{upcoming.map((e) => <QARow key={e.id} ev={e} upcoming/>)}</div>
        </div>
      )}
      {past.length > 0 && (
        <div>
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-3">PAST RECORDINGS</div>
          <div className="space-y-3">{past.map((e) => <QARow key={e.id} ev={e}/>)}</div>
        </div>
      )}
    </div>
  );
}

function QARow({ ev, upcoming }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5 flex justify-between items-start gap-3 flex-wrap" data-testid={`qa-row-${ev.id}`}>
      <div>
        <div className="font-display text-lg text-[#F4F1EA]">{ev.title}</div>
        <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] uppercase mt-1">{new Date(ev.scheduled_at).toLocaleString()}</div>
        {ev.description && <p className="font-body text-[12px] text-[#B5B0A7] mt-2 max-w-lg">{ev.description}</p>}
      </div>
      {upcoming && ev.meeting_url && (
        <a href={ev.meeting_url} target="_blank" rel="noreferrer" className="px-4 py-2 bg-[#C9A055] text-black font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2" data-testid={`qa-join-${ev.id}`}><Calendar size={12}/> Join</a>
      )}
      {!upcoming && ev.recording_url && (
        <a href={ev.recording_url} target="_blank" rel="noreferrer" className="px-4 py-2 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2 transition-colors" data-testid={`qa-watch-${ev.id}`}><Play size={11}/> Watch Recording</a>
      )}
    </div>
  );
}

function Skeleton() {
  return <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3].map((i) => <div key={i} className="border border-white/10 bg-[#0a0a0a] h-64 animate-pulse"/>)}</div>;
}
